import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, RotateCw, Settings, Bug } from 'lucide-react';
import jsQR from 'jsqr';
import { Z_INDICES } from '../lib/zIndices';
import { useKeyboardOffset } from '../hooks/useKeyboardOffset';

/**
 * Returns true when the native Capacitor shell has registered the
 * "openappsettings" plugin. False in plain web/PWA and false on any
 * older Android APK that shipped BEFORE we added the plugin — so the
 * "Open App Settings" button silently hides itself on those users
 * rather than showing them a dead button.
 */
function canOpenAppSettings() {
  try {
    return !!(window?.Capacitor?.Plugins?.openappsettings?.open);
  } catch (_) {
    return false;
  }
}

/**
 * Runtime handle to the native @capacitor/camera plugin. Undefined
 * on plain web and on ancient APKs that predate the plugin. We NEVER
 * import '@capacitor/camera' at module level because it would crash
 * old APKs' JS boot (same failure mode we hit with
 * capacitor-open-app-settings).
 */
function getNativeCameraPlugin() {
  try {
    return window?.Capacitor?.Plugins?.Camera || null;
  } catch (_) {
    return null;
  }
}

/**
 * Force the OS-level Android camera permission prompt to appear.
 *
 * WHY THIS EXISTS (2026-07): our Capacitor Android app loads a
 * REMOTE server URL (`allurexp.com`) instead of a bundled webDir.
 * On that setup, `navigator.mediaDevices.getUserMedia()` inside the
 * WebView does NOT reliably trigger the Android runtime CAMERA
 * permission dialog — the WebView's `BridgeWebChromeClient` treats
 * the remote origin as third-party and silently rejects the permission
 * request when the app hasn't been granted `android.permission.CAMERA`
 * at the OS layer yet. Reported user symptom:
 *
 *   "The app never asks me for camera permission — even after uninstall
 *    + reinstall. It just says denied immediately."
 *
 * The @capacitor/camera plugin's `requestPermissions()` bypasses the
 * WebView entirely and calls `ActivityCompat.requestPermissions()`
 * natively, which DOES show the OS prompt. Once granted at the OS
 * layer, subsequent `getUserMedia()` calls from the WebView succeed
 * because Capacitor's WebChromeClient auto-grants matching resources.
 *
 * Returns:
 *   'granted'   — proceed to getUserMedia
 *   'denied'    — user tapped "Don't allow" (show retry / open-settings)
 *   'unavailable' — plugin not present (plain web / very old APK); the
 *                   caller should fall back to plain getUserMedia so
 *                   web/PWA users still hit the browser prompt path.
 */
async function ensureNativeCameraPermission() {
  const plugin = getNativeCameraPlugin();
  if (!plugin) return 'unavailable';
  try {
    let status = null;
    if (typeof plugin.checkPermissions === 'function') {
      try {
        status = await plugin.checkPermissions();
      } catch (_) { status = null; }
    }
    if (status?.camera === 'granted') return 'granted';
    if (typeof plugin.requestPermissions === 'function') {
      const result = await plugin.requestPermissions({ permissions: ['camera'] });
      if (result?.camera === 'granted') return 'granted';
      return 'denied';
    }
    return 'unavailable';
  } catch (err) {
    console.warn('Native camera permission request failed:', err?.message || err);
    return 'unavailable';
  }
}

/**
 * Lazily open the native Android/iOS App Settings page so a user who
 * previously tapped "Deny & don't ask again" can grant camera access
 * without hunting through system Settings themselves.
 *
 * IMPORTANT (2026-07): we intentionally do NOT `import` any
 * `capacitor-open-app-settings` npm wrapper — not at top level, and
 * not as a webpack dynamic `import()` either. The plugin was removed
 * from `package.json` after the beta version caused (1) a webpack
 * strict-ESM build failure, (2) an Android Gradle Sync "Namespace
 * not specified" failure, and (3) a blank-screen crash on old APKs
 * that lacked the native side. Instead we reach into the Capacitor
 * runtime plugin registry that the native shell populates at boot
 * IF a future stable plugin is ever wired in. Until then this call
 * silently no-ops and the button is hidden.
 */
async function openAppSettingsLazy() {
  try {
    const plugin = window?.Capacitor?.Plugins?.openappsettings;
    if (plugin && typeof plugin.open === 'function') {
      await plugin.open();
      return true;
    }
    return false;
  } catch (err) {
    console.warn('openAppSettings unavailable:', err?.message || err);
    return false;
  }
}
/**
 * QR scanner modal used by the Venue Owner, Promoted Venue Admin, and
 * Regular Staff scan pages. Every open of this modal MUST re-check camera
 * access — if the user hasn't granted permission yet, the OS prompt is
 * triggered by the getUserMedia() call. If they've granted before, the
 * camera streams straight away without any extra confirmation.
 *
 * State machine:
 *   idle → requesting → { streaming | denied | unsupported }
 * The user can retry from `denied` (re-triggers getUserMedia which either
 * shows the OS prompt again or, if permanently denied, resolves the same
 * error — in which case they must enable it from Android settings).
 */
export const CameraQrScannerModal = ({
  isOpen,
  onClose,
  title,
  manualValue,
  onManualChange,
  onScanCode,
  onDemo,
  scanning,
  modalTestId,
  manualInputTestId,
  confirmButtonTestId,
  demoButtonTestId,
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const detectorRef = useRef(null);
  const detectorFailsRef = useRef(0);
  const canvasRef = useRef(null);
  const scanningRef = useRef(false);
  const onScanCodeRef = useRef(onScanCode);
  const onCloseRef = useRef(onClose);
  const [status, setStatus] = useState('idle'); // 'idle' | 'requesting' | 'streaming' | 'denied' | 'unsupported'
  const [supportsDetection, setSupportsDetection] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Shift the modal up when the on-screen keyboard opens so the
  // manual-entry input + Confirm button stay visible above the IME.
  const keyboardOffset = useKeyboardOffset(isOpen);

  // ─── LIVE DIAGNOSTIC HUD ────────────────────────────────────────────────
  // The user opens the app on their real Android device, taps the small
  // 🐞 bug icon at the corner of the camera frame, points the camera at a
  // guest QR, and gets live proof of where the failure is:
  //   • frames > 0 but decoded === 0 → camera fine, decoder blind
  //   • frames === 0                  → scan loop never started
  //   • decoded > 0 but lookups === 0 → decoded value never reaches parent
  //   • lookups > 0 but no match      → backend / QR format mismatch
  const [showDiag, setShowDiag] = useState(false);
  const [diag, setDiag] = useState({
    frames: 0,
    videoW: 0,
    videoH: 0,
    decoderMode: 'init',    // 'init' | 'BarcodeDetector' | 'BarcodeDetector-disabled' | 'jsQR' | 'jsQR-only'
    detectorFails: 0,
    decoded: 0,
    lastDecoded: '',
    lastDecodedAt: 0,
    lookups: 0,
    lastLookupOk: null,     // null | true | false
  });
  const diagRef = useRef(diag);
  useEffect(() => { diagRef.current = diag; }, [diag]);
  // Batched diag updates: mutate the ref every frame (cheap) and flush to
  // React state every 500ms so the HUD paints without hammering re-renders
  // on every requestAnimationFrame tick (which would tank camera FPS).
  const pendingDiagRef = useRef(null);
  const bumpDiag = useCallback((patch) => {
    pendingDiagRef.current = { ...(pendingDiagRef.current || diagRef.current), ...patch };
  }, []);
  useEffect(() => {
    if (!isOpen) return undefined;
    const iv = window.setInterval(() => {
      if (pendingDiagRef.current) {
        setDiag(pendingDiagRef.current);
        pendingDiagRef.current = null;
      }
    }, 500);
    return () => window.clearInterval(iv);
  }, [isOpen]);
  // `firstFrame` flips to true on the video's first `playing` event so
  // the "Camera loading…" spinner overlay fades out the moment real
  // camera frames start rendering. Without this, on slower Android
  // devices there's a ~300–800ms gap where the OS has granted
  // permission and the stream is attached but the WebView hasn't
  // painted the first frame yet, showing a jarring black square.
  const [firstFrame, setFirstFrame] = useState(false);

  useEffect(() => { scanningRef.current = scanning; }, [scanning]);
  useEffect(() => { onScanCodeRef.current = onScanCode; }, [onScanCode]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const stopScanner = useCallback(() => {
    if (frameRef.current) {
      window.clearTimeout(frameRef.current);
      frameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch (_) { /* ignore */ }
    }
    detectorRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopScanner();
      setStatus('idle');
      setFirstFrame(false);
      return undefined;
    }
    setFirstFrame(false);
    // Reset diagnostic counters on each open so the HUD starts fresh
    pendingDiagRef.current = null;
    setDiag({
      frames: 0,
      videoW: 0,
      videoH: 0,
      decoderMode: 'init',
      detectorFails: 0,
      decoded: 0,
      lastDecoded: '',
      lastDecodedAt: 0,
      lookups: 0,
      lastLookupOk: null,
    });

    let cancelled = false;

    const scanFrame = async () => {
      if (
        cancelled ||
        !videoRef.current ||
        scanningRef.current ||
        videoRef.current.readyState < 2
      ) {
        frameRef.current = window.setTimeout(scanFrame, 250);
        return;
      }
      // Bump frame counter and current video dimensions on every tick.
      // Proves to the on-device HUD that the scan loop is actually
      // running and that the WebView is delivering real camera frames.
      const currentFrames = (pendingDiagRef.current?.frames ?? diagRef.current.frames) + 1;
      bumpDiag({
        frames: currentFrames,
        videoW: videoRef.current.videoWidth || 0,
        videoH: videoRef.current.videoHeight || 0,
      });
      try {
        let rawValue = null;
        let usedPath = null; // 'BarcodeDetector' | 'jsQR' — whoever DECODED this frame

        // Path A — native BarcodeDetector (fast, GPU-accelerated on
        // desktop Chrome and modern Android WebViews).
        //
        // CRITICAL (2026 fix): this MUST be isolated in its own
        // try/catch. On many Android WebViews `BarcodeDetector` exists
        // and advertises `qr_code`, but `detect()` THROWS on every call
        // (Google Play Services barcode module missing / not
        // downloaded). Previously the throw jumped straight to the
        // outer catch, silently SKIPPING the jsQR fallback below on
        // every frame — the exact "camera opens but never detects"
        // bug reported on the production Android app. Now a failing
        // detector is disabled after 3 consecutive throws and jsQR
        // takes over completely.
        if (detectorRef.current) {
          try {
            const codes = await detectorRef.current.detect(videoRef.current);
            rawValue = codes?.[0]?.rawValue?.trim() || null;
            if (rawValue) usedPath = 'BarcodeDetector';
            detectorFailsRef.current = 0;
          } catch (detectorError) {
            detectorFailsRef.current += 1;
            bumpDiag({ detectorFails: detectorFailsRef.current });
            if (detectorFailsRef.current >= 3) {
              console.warn(
                'BarcodeDetector unreliable on this device — switching to jsQR only:',
                detectorError?.message || detectorError
              );
              detectorRef.current = null;
              bumpDiag({ decoderMode: 'BarcodeDetector-disabled' });
            }
          }
        }

        // Path B — jsQR fallback. Runs whenever BarcodeDetector is
        // absent (older Android WebViews, iOS Safari <17), disabled
        // after repeated failures, OR returned nothing on this frame.
        //
        // CENTER-CROP AT NATIVE RESOLUTION (2026-fix): the previous
        // implementation downscaled the WHOLE frame to 480px on the
        // long edge before decoding, which shrunk each QR module to
        // 3–4 pixels — jsQR could no longer detect finder patterns
        // and never decoded, especially against phone-screen sources.
        // Now we center-crop a square matching the visible aim
        // reticle (~78% of the shorter edge) and feed that crop to
        // jsQR at native resolution — every QR module stays ≥6 px,
        // giving jsQR a real chance to lock on. A safety cap of
        // 720 px protects lower-end Androids from CPU spikes on
        // 1080p+ streams.
        if (!rawValue && canvasRef.current && videoRef.current) {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          if (vw > 0 && vh > 0) {
            const cropSize = Math.floor(Math.min(vw, vh) * 0.78);
            const targetSize = Math.min(cropSize, 720);
            const sx = Math.floor((vw - cropSize) / 2);
            const sy = Math.floor((vh - cropSize) / 2);
            if (canvas.width !== targetSize) canvas.width = targetSize;
            if (canvas.height !== targetSize) canvas.height = targetSize;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(video, sx, sy, cropSize, cropSize, 0, 0, targetSize, targetSize);
              const imageData = ctx.getImageData(0, 0, targetSize, targetSize);
              // attemptBoth also decodes inverted (light-on-dark) QR
              // codes — e.g. a guest phone rendering the pass in dark
              // mode or with an inverted accessibility theme.
              const result = jsQR(imageData.data, targetSize, targetSize, {
                inversionAttempts: 'attemptBoth',
              });
              if (result?.data) {
                rawValue = result.data.trim();
                usedPath = 'jsQR';
              }
            }
          }
        }

        if (rawValue) {
          const nextDecoded = (pendingDiagRef.current?.decoded ?? diagRef.current.decoded) + 1;
          bumpDiag({
            decoded: nextDecoded,
            lastDecoded: rawValue.length > 60 ? `${rawValue.slice(0, 57)}…` : rawValue,
            lastDecodedAt: Date.now(),
            decoderMode: usedPath || diagRef.current.decoderMode,
          });
          const nextLookups = (pendingDiagRef.current?.lookups ?? diagRef.current.lookups) + 1;
          bumpDiag({ lookups: nextLookups });
          let succeeded = false;
          try {
            succeeded = await onScanCodeRef.current(rawValue);
            bumpDiag({ lastLookupOk: !!succeeded });
          } catch (lookupErr) {
            console.error('QR lookup handler threw:', lookupErr);
            bumpDiag({ lastLookupOk: false });
          }
          if (succeeded) {
            onCloseRef.current();
            return;
          }
        }
      } catch (error) {
        console.error('QR detection failed:', error);
      }
      frameRef.current = window.setTimeout(scanFrame, 250);
    };

    const startScanner = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) setStatus('unsupported');
        return;
      }

      setStatus('requesting');

      // STEP 1 — On Capacitor Android, force the OS-level runtime CAMERA
      // permission prompt via @capacitor/camera's native path. The
      // WebView's `getUserMedia()` alone won't trigger it when the app
      // is loading a remote origin (server.url), so users never saw
      // the OS prompt and were silently denied on cold install. Web/PWA
      // users skip this branch (plugin unavailable) and hit the browser
      // prompt via getUserMedia directly.
      const nativeStatus = await ensureNativeCameraPermission();
      if (cancelled) return;
      if (nativeStatus === 'denied') {
        setStatus('denied');
        return;
      }

      try {
        // STEP 2 — WebView getUserMedia. On native Android this now
        // succeeds because the OS permission was just granted above.
        // On web/PWA it triggers the browser's own permission prompt
        // as it always has.
        //
        // RESOLUTION HINTS (2026-fix): explicitly ask for 1280×720
        // instead of accepting Chrome's default 640×480. On the low
        // default resolution, a QR that occupies ~50% of the frame
        // ends up with each module only 3–4 pixels wide — below what
        // jsQR can reliably decode, especially when the source QR is
        // a phone screen (moiré + glare). `ideal` is a soft hint: if
        // the device can't do 720p, the browser falls back gracefully
        // and getUserMedia still succeeds.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch (_) { /* autoplay policies */ }
        }
        setStatus('streaming');

        // Try to hook up the native BarcodeDetector (fast path). If
        // it's missing or doesn't advertise `qr_code`, that's fine —
        // scanFrame will silently fall through to jsQR on every tick.
        detectorRef.current = null;
        detectorFailsRef.current = 0;
        let initialDecoderMode = 'jsQR-only';
        try {
          if ('BarcodeDetector' in window) {
            const supportedFormats = await window.BarcodeDetector.getSupportedFormats();
            if (supportedFormats.includes('qr_code')) {
              detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
              initialDecoderMode = 'BarcodeDetector';
            }
          }
        } catch (err) {
          console.warn('BarcodeDetector init failed, using jsQR fallback:', err?.message || err);
          detectorRef.current = null;
          initialDecoderMode = 'jsQR-only';
        }
        bumpDiag({ decoderMode: initialDecoderMode });

        // Auto-detect is ALWAYS supported now because jsQR runs in
        // pure JS on the canvas. This flips the helper text to the
        // "Point the camera at the guest QR code" copy on every
        // device — including older Android WebViews where the native
        // BarcodeDetector was missing and the app used to silently
        // force manual entry.
        setSupportsDetection(true);
        frameRef.current = window.setTimeout(scanFrame, 250);
      } catch (error) {
        console.error('Camera start failed:', error);
        if (!cancelled) setStatus('denied');
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      stopScanner();
    };
    // `attempt` is included so the "Try again" button can re-run the effect.
  }, [isOpen, attempt]);

  const handleClose = () => {
    stopScanner();
    onCloseRef.current();
  };

  const handleRetry = () => {
    stopScanner();
    setStatus('requesting');
    setAttempt((n) => n + 1);
  };

  const handleOpenSettings = async () => {
    await openAppSettingsLazy();
  };

  const handleConfirm = async () => {
    const succeeded = await onScanCodeRef.current(manualValue);
    if (succeeded) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const requesting = status === 'requesting';
  const denied = status === 'denied';
  const unsupported = status === 'unsupported';
  const streaming = status === 'streaming';

  let helperText;
  if (streaming && !firstFrame) helperText = 'Starting the camera…';
  else if (streaming) helperText = 'Point the camera at the guest QR code to scan automatically.';
  else if (requesting) helperText = 'Allow camera access when your phone asks — this is a one-time prompt.';
  else if (denied) helperText = attempt >= 1
    ? "Camera still blocked. Tap 'Open App Settings' to enable it manually — or paste the guest's QR code below."
    : "Tap 'Try again' below to grant camera access — or paste the guest's QR code manually.";
  else if (unsupported) helperText = 'This device or browser does not expose a camera to the app. Paste the QR code below.';
  else helperText = ' ';

  return (
    <div
      data-testid={modalTestId}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDICES.modalOverlay,
        backgroundColor: 'rgba(0,0,0,0.55)',
        display: 'flex',
        // When the keyboard is open, align the modal to the top of the
        // visible viewport instead of centering it against the full
        // window — centering plus translateY can still leave the title
        // off-screen when the IME covers > 40% of the screen.
        alignItems: keyboardOffset > 0 ? 'flex-start' : 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={handleClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          backgroundColor: 'var(--surface)',
          borderRadius: 18,
          padding: '24px 20px',
          width: '100%',
          maxWidth: 340,
          // Shrink the modal to fit above the keyboard. When closed,
          // keep the usual 90vh cap. visualViewport.height ≈
          // 100vh - keyboardOffset, so this effectively equals the
          // visible area minus a 32 px breathing margin.
          maxHeight: keyboardOffset > 0
            ? `calc(100vh - ${keyboardOffset + 32}px)`
            : '90vh',
          overflowY: 'auto',
          boxShadow: '0 16px 40px rgba(0,0,0,0.28)',
          border: '1px solid var(--card-border)',
          transition: 'max-height 0.2s ease-out',
        }}
      >
        <h3 style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 20,
          fontWeight: 700,
          color: 'var(--text)',
          textAlign: 'center',
          marginBottom: 14,
        }}>
          {title}
        </h3>

        <div
          data-testid={`${modalTestId}-camera-frame`}
          style={{
            borderRadius: 16,
            overflow: 'hidden',
            background: '#120F1A',
            width: '100%',
            // Fixed 1:1 aspect ratio keeps the camera preview the same
            // size regardless of the camera's native resolution. When
            // the keyboard is open the user is typing manually, so
            // collapse the preview to a thin strip to free space for
            // the input + Confirm button above the IME.
            aspectRatio: keyboardOffset > 0 ? 'auto' : '1 / 1',
            height: keyboardOffset > 0 ? 72 : undefined,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'height 0.2s ease-out',
          }}
        >
          {/*
            IMPORTANT (2026-07 bug fix): the <video> element MUST stay
            mounted at all times — NOT gated on `streaming` — otherwise
            `videoRef.current` is null at the exact moment we try to
            assign `srcObject = stream` right after `getUserMedia()`
            resolves. That caused the "granted permission but black
            frame" bug on Android APK: the state DID flip to
            'streaming', helper text changed, but the stream never
            attached because the ref was null one tick earlier.
            We hide the element with `display: none` until streaming
            starts so the "Waiting for permission" placeholder can
            still occupy the box in the meantime.
          */}
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            data-testid={`${modalTestId}-video`}
            onPlaying={() => setFirstFrame(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: streaming ? 'block' : 'none',
            }}
          />

          {/*
            Offscreen canvas used by the jsQR fallback decoder. It's
            never displayed — we just draw each video frame into it
            (downscaled to 480px on the long edge) so jsQR can read
            the ImageData buffer. Kept as a sibling of <video> so its
            ref is populated in the exact same commit as the video's,
            avoiding the same "ref-null-on-first-tick" pitfall
            documented above the <video> element.
          */}
          <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{ display: 'none' }}
          />

          {/*
            "Camera loading…" spinner overlay: appears the instant the
            stream is attached (status === 'streaming') and disappears
            as soon as the video element fires its first `playing`
            event. Covers the ~300–800ms Android WebView gap where the
            frame is still black between "permission granted" and
            "first camera frame painted".
          */}
          {streaming && !firstFrame && (
            <div
              data-testid={`${modalTestId}-camera-loading`}
              style={{
                position: 'absolute',
                inset: 0,
                background: '#120F1A',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                color: '#F5F5F7',
                pointerEvents: 'none',
              }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  border: '2.5px solid rgba(255,255,255,0.16)',
                  borderTopColor: 'rgba(255,255,255,0.92)',
                  boxSizing: 'border-box',
                }}
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 13,
                  margin: 0,
                  letterSpacing: '0.02em',
                }}
              >
                Camera loading…
              </motion.p>
            </div>
          )}

          {requesting && (
            <div
              data-testid={`${modalTestId}-requesting`}
              style={{
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                color: '#F5F5F7',
              }}
            >
              <Camera size={34} strokeWidth={1.6} color="#F5F5F7" />
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, textAlign: 'center', lineHeight: 1.5, margin: 0 }}>
                Waiting for camera permission…
              </p>
            </div>
          )}

          {(denied || unsupported) && (
            <div
              data-testid={`${modalTestId}-permission-denied`}
              style={{
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                color: '#F5F5F7',
              }}
            >
              <Camera size={34} strokeWidth={1.6} color="#FF8080" />
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, textAlign: 'center', lineHeight: 1.5, margin: 0 }}>
                {denied
                  ? 'Camera permission is required to scan.'
                  : 'Camera is unavailable on this device.'}
              </p>
              {denied && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={handleRetry}
                    data-testid={`${modalTestId}-retry-btn`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 16px',
                      borderRadius: 999,
                      background: 'var(--accent-purple)',
                      color: '#FFFFFF',
                      border: 'none',
                      fontFamily: "'Inria Serif', serif",
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <RotateCw size={14} strokeWidth={2.2} />
                    Try again
                  </button>
                  {attempt >= 1 && canOpenAppSettings() && (
                    <button
                      type="button"
                      onClick={handleOpenSettings}
                      data-testid={`${modalTestId}-open-settings-btn`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '7px 14px',
                        borderRadius: 999,
                        background: 'transparent',
                        color: '#F5F5F7',
                        border: '1px solid rgba(255,255,255,0.4)',
                        fontFamily: "'Inria Serif', serif",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Settings size={13} strokeWidth={2} />
                      Open App Settings
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {streaming && firstFrame && (
            <div style={{
              position: 'absolute',
              inset: 20,
              border: '2px solid rgba(255,255,255,0.8)',
              borderRadius: 16,
              pointerEvents: 'none',
            }} />
          )}

          {/*
            ─── LIVE DIAGNOSTIC HUD ──────────────────────────────────
            Small 🐞 button top-right of the camera frame. Tap to
            toggle a live overlay showing frame count, video WxH,
            active decoder, last decoded QR value, and lookup result.
            Purpose: on a real Android device, this lets the user
            immediately see whether the failure is the camera, the
            decoder, or the backend lookup — without needing adb logs.
          */}
          <button
            type="button"
            onClick={() => setShowDiag((v) => !v)}
            data-testid={`${modalTestId}-diag-toggle`}
            aria-label="Toggle scanner diagnostics"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 32,
              height: 32,
              borderRadius: 999,
              background: showDiag ? 'rgba(229,57,53,0.85)' : 'rgba(0,0,0,0.45)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              zIndex: 3,
            }}
          >
            <Bug size={16} strokeWidth={2} />
          </button>

          {showDiag && (
            <div
              data-testid={`${modalTestId}-diag-hud`}
              style={{
                position: 'absolute',
                left: 8,
                right: 8,
                bottom: 8,
                background: 'rgba(0,0,0,0.78)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 10,
                padding: '8px 10px',
                fontFamily: 'monospace',
                fontSize: 11,
                lineHeight: 1.45,
                color: '#F5F5F7',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <div>frames: <b>{diag.frames}</b> · video: {diag.videoW}×{diag.videoH}</div>
              <div>decoder: <b>{diag.decoderMode}</b> · det-fails: {diag.detectorFails}</div>
              <div>decoded: <b>{diag.decoded}</b> · lookups: {diag.lookups} · ok: {diag.lastLookupOk === null ? '—' : diag.lastLookupOk ? 'yes' : 'no'}</div>
              <div style={{ wordBreak: 'break-all', opacity: diag.lastDecoded ? 1 : 0.55 }}>
                last: {diag.lastDecoded || '(nothing decoded yet)'}
              </div>
            </div>
          )}
        </div>

        <p style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 14,
          color: 'var(--text-2)',
          textAlign: 'center',
          margin: '10px 0 16px',
          lineHeight: 1.5,
          minHeight: 20,
        }}>
          {helperText}
        </p>

        {/*
          NOTE (2026-02 bug fix): `autoFocus` was previously set on this
          input, which caused the phone's on-screen keyboard to pop up
          the moment the scanner modal opened — even when the camera
          was successfully streaming and the user only intended to
          scan. The manual code entry is a FALLBACK, not the primary
          flow, so we no longer steal focus on mount. Users who need
          to type still tap the field, which then focuses and shows
          the keyboard normally.
        */}
        <input
          type="text"
          value={manualValue}
          onChange={(event) => onManualChange(event.target.value.toUpperCase())}
          onFocus={(event) => {
            // Safety net for older WebViews without visualViewport:
            // scroll the input into view so the modal's overflow:auto
            // keeps it visible above the keyboard. 'nearest' avoids
            // scrolling the title out of view when the content is
            // taller than the visible area.
            const target = event.target;
            window.setTimeout(() => {
              try {
                target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              } catch (_) { /* older WebView — ignore */ }
            }, 300);
          }}
          placeholder="LOU8Y5"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          data-testid={manualInputTestId}
          style={{
            width: '100%',
            padding: '12px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 18,
            letterSpacing: '0.15em',
            textAlign: 'center',
            textTransform: 'uppercase',
            border: '2px solid var(--card-border)',
            borderRadius: 10,
            outline: 'none',
            marginBottom: 12,
            background: 'var(--surface-2)',
            color: 'var(--text)',
          }}
        />

        <button
          onClick={handleConfirm}
          disabled={scanning}
          data-testid={confirmButtonTestId}
          style={{
            width: '100%',
            padding: '12px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 18,
            fontWeight: 700,
            color: '#FFFFFF',
            backgroundColor: '#00B341',
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
            marginBottom: 8,
            opacity: scanning ? 0.6 : 1,
          }}
        >
          {scanning ? 'Scanning...' : 'Confirm Scan'}
        </button>

        {onDemo && (
          <button
            onClick={onDemo}
            disabled={scanning}
            data-testid={demoButtonTestId}
            style={{
              width: '100%',
              padding: '10px',
              fontFamily: "'Inria Serif', serif",
              fontSize: 16,
              color: 'var(--text-2)',
              backgroundColor: 'var(--surface-2)',
              border: '1px solid var(--card-border)',
              borderRadius: 12,
              cursor: 'pointer',
            }}
          >
            Demo: Scan Guest Pass
          </button>
        )}
      </div>
    </div>
  );
};

export default CameraQrScannerModal;
